import { ContentCopy, Edit, LockReset, Share, ViewInAr, Warning } from "@mui/icons-material";
import { Chip, IconButton, LinearProgress, Stack, Tooltip } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { useContext } from "react";
import { SocketContext } from "../../contexts";
import { useAppDispatch } from "../../redux";
import { useLazyGetDocumentByIdQuery, useUpdateMaterialDocumentMutation, useUploadDocumentMutation } from "../../redux/services";
import { closeBackdrop, openBackdrop } from "../../redux/slices/backdrop";
import { UploadPercentage } from "../../redux/slices/utils";
import { AppRoutes } from "../../router/routes";
import { blobUrlToFile, LocalStorageKeys, parseJwt } from "../../utils";
import { BorderLinearProgress } from "../borderedLinearProgress";
import { DocumentAction } from "../documentAction";
import { DownloadDoc, onlyOfficePreviableTypes } from "../downloadDoc";

export const DocumentActions: React.FC<{
    row: any, uploads: UploadPercentage[],
    forceRelease?: any, handleDuplicate?: any, handleShare?: any,
    disablePreview?: boolean, handleDataSheet?: (row: any) => void, onDocumentUploadComplete?: () => any
}> = ({ row, uploads, forceRelease, handleDuplicate, disablePreview, handleShare, handleDataSheet, onDocumentUploadComplete }) => {

    const { receivedEvents } = useContext(SocketContext);

    const dispatch = useAppDispatch();

    const [getDocumentById] = useLazyGetDocumentByIdQuery();
    const [updateDocumentForFileUpload] = useUpdateMaterialDocumentMutation();
    const [uploadDocument] = useUploadDocumentMutation();

    const [status, updateStatus] = React.useState<string>(row.status);
    const interval = React.useRef<NodeJS.Timeout>();

    const fileType = row?.file_name?.split('.')?.pop();

    const token = localStorage.getItem(LocalStorageKeys.authToken) || "";

    const tokenData = token ? parseJwt(token) : {};

    let uploadDetail = uploads.find(u => u.id === row._id);

    const failedFiles = JSON.parse(sessionStorage.getItem(LocalStorageKeys.failedFiles) || "[]");

    let currentFile = failedFiles.find((u: any) => row?._id === u.id);

    const handleReUpload = async (currentFile: any, row: any) => {
        dispatch(openBackdrop("Re Uploading the file"))
        await updateDocumentForFileUpload({
            document_id: row?._id,
            update_document: true,
            file_name: currentFile?.file?.name,
        }).then(async (res: any) => {
            const file = await blobUrlToFile(currentFile?.file?.url, currentFile?.file?.name, currentFile?.file?.type);
            updateStatus("pending");
            uploadDocument({ data: { _id: row?._id }, url: res?.data?.url, file, onDocumentUploadComplete });
            sessionStorage.setItem(LocalStorageKeys.failedFiles, JSON.stringify([...JSON.parse(localStorage.getItem(LocalStorageKeys.failedFiles) || "[]").filter((f: any) => f.id !== row?._id)]))
        }).catch((error: any) => {
            enqueueSnackbar(error?.data?.title || error?.data || "Uploading failed retry after some time")
        }).finally(() => {
            dispatch(closeBackdrop());
        });
    }

    const refetchDocumentStatus = () => {
        getDocumentById({ document_id: row._id }).unwrap().then((res: any) => {
            if (!res.title) {
                updateStatus(res.status);
                if (res.status !== 'pending') {
                    clearInterval(interval.current);
                }
            }
        }).catch((err) => {
            console.error("Error Fetching Document: ", JSON.stringify(err));
        });
    };

    React.useEffect(() => {
        let socketData = receivedEvents?.["document-status"]?.[row._id] || {};
        if (status === 'pending' && Object.keys(socketData).length > 0) {
            let payload = socketData;
            if (payload.status === 'saved') {
                updateStatus("saved");
            } else {
                updateStatus("failed");
            }
        }
        // eslint-disable-next-line
    }, [receivedEvents?.["document-status"], receivedEvents?.["document-status"]?.[row._id]]);

    React.useEffect(() => {
        if (uploadDetail?.percentage === 100) {
            updateStatus("saved");
        }
        // eslint-disable-next-line
    }, [uploadDetail]);

    React.useEffect(() => {
        if (currentFile?.id) {
            updateStatus("failed");
        }
        // eslint-disable-next-line
    }, [currentFile]);

    React.useEffect(() => {
        if (status === 'pending' && !uploadDetail) {
            refetchDocumentStatus();
            interval.current = setInterval(() => {
                refetchDocumentStatus();
            }, 60 * 1000);
        }

        return () => clearInterval(interval.current);
        // eslint-disable-next-line
    }, []);

    if (uploadDetail?.status === 'pending' && uploadDetail?.percentage < 100) {
        return <Stack direction={"row"} alignItems={"center"} sx={{ width: "100%" }}>
            <BorderLinearProgress variant="determinate" value={uploadDetail?.percentage ?? 99} />
        </Stack>
    }

    if (status === "failed" && currentFile?.file) {
        return <Stack direction={"row"} alignItems={"center"} onClick={(e) => {
            e.stopPropagation();
            handleReUpload(currentFile as UploadPercentage, row);
        }}>
            <Chip size="small" icon={<Warning />} sx={{ pl: 1, cursor: "pointer" }} variant="outlined" label="Re-Upload." color="warning" />
        </Stack>
    }

    if (status === "failed" && !currentFile?.file) {
        return <Stack direction={"row"} alignItems={"center"}>
            <Chip size="small" icon={<Warning />} sx={{ pl: 1 }} variant="outlined" label="Upload Failed" color="error" />
        </Stack>
    }

    if (status === 'pending') {
        return <Stack direction={"row"} alignItems={"center"} sx={{ width: "100%" }}>
            <Tooltip title='Uploading... Please check after some time'>
                <LinearProgress sx={{ width: "100%", height: "10px", borderRadius: "8px" }} />
            </Tooltip>
        </Stack>
    }

    let currentAction = !(onlyOfficePreviableTypes.includes(fileType) || fileType === 'pdf') && fileType === 'xml' && row?.activity?.opened && tokenData?.sub !== row?.activity?.user_id ?
        <Tooltip title={`Document locked, Since user "${row?.activity?.user_name ?? "Unknown"}" is still editing. Click to enforce Force Release`}>
            <IconButton onClick={(e) => {
                e.stopPropagation();
                if (forceRelease) {
                    forceRelease(row)
                }
            }}>
                <LockReset fontSize="small" color="error" />
            </IconButton>
        </Tooltip> : null;


    return <Stack direction={"row"} alignItems={"center"}>
        <DocumentAction
            id={row._id}
            status={status}
            children={currentAction ? currentAction :
                <Tooltip title={row?.released ? `${row?.sequence_id} Revision is published` : [...onlyOfficePreviableTypes, 'pdf', 'xml'].includes(fileType) ? `Edit the Document ${row?.sequence_id}` : `Editing not supported for the document`}>
                    <span>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onlyOfficePreviableTypes.includes(fileType) || fileType === 'pdf') {
                                    window.open(`${AppRoutes.officeEditor}?document_id=${row._id}`)
                                } else {
                                    window.open(`${AppRoutes.drawioDiagramEditor(row._id)}?file_name=${row.file_name}`)
                                }
                            }}
                            disabled={![...onlyOfficePreviableTypes, 'pdf', 'xml'].includes(fileType) || row?.released}
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            }
        />

        <DownloadDoc preview={true} id={row._id} url={row.url} status={status} fileType={disablePreview ? "" : fileType ?? "Unknown"} />
        <DownloadDoc id={row._id} url={row.url} status={status} />

        {handleDuplicate &&
            <Tooltip title="Make a Duplicate">
                <IconButton
                    size="small"
                    // sx={{ margin: "auto" }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate && handleDuplicate(row)
                    }}
                >
                    <ContentCopy fontSize="small" />
                </IconButton>
            </Tooltip>
        }

        {handleShare && <DocumentAction
            id={row._id}
            status={status}
            children={
                <Tooltip title={`Share the ${row?.sequence_id}-r${row?.version}`}>
                    <span>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleShare && handleShare(row)
                            }}
                        >
                            <Share fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            }
        />}

        {handleDataSheet &&
            <Tooltip title="Link a Datasheet">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDataSheet(row);
                    }}
                >
                    <ViewInAr fontSize="small" />
                </IconButton>
            </Tooltip>
        }
    </Stack>;
}
